import {
  INFO_EMAIL,
  SUPPORT_EMAIL,
  SUPPORT_PHONE,
} from '@/client/accounts/types/account.type';
import { CYBERPEDIA_URL } from '@/common/constants/external-urls';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { FlexContainer } from '@/ui/styled-ui';
import { Avatar } from 'primereact/avatar';
import { OverlayPanel, OverlayPanelProps } from 'primereact/overlaypanel';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledOverlayPanel = styled(OverlayPanel)`
  min-width: 300px;
  border-radius: var(--medium-border-radius);
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.1), 0 4px 16px 0 rgba(0, 0, 0, 0.1);
  h4 {
    font-size: var(--medium-font-size);
    line-height: var(--medium-line-height);
    color: var(--black-main);
    margin: 0;
  }

  a {
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
    font-weight: 500;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: var(--black-main);
    text-decoration: none;
    &:hover {
      color: var(--red-main);
      cursor: pointer;
    }
  }

  p {
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
    font-weight: 500;
    color: var(--black-main);
    margin-block: var(--xxsmall-padding);
  }

  .cyberpedia-link {
    text-decoration: underline;
  }

  .support-email {
    font-weight: 700;
    text-decoration: underline;
  }

  .csm-name {
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    font-weight: 700;
  }
  .csm-email {
    text-decoration: underline;
  }
`;

const StyledAvatar = styled(Avatar)`
  &.p-avatar {
    border-radius: var(--xsmall-border-radius);
    background-color: var(--beige-main);
    overflow: hidden;
    &.p-avatar-xl {
      width: 70px !important;
      height: 70px !important;
    }

    .pi {
      font-size: var(--heading-large-font-size);
      line-height: var(--heading-large-line-height);
      color: var(--gray-darker);
    }
  }
`;

type GetHelpOverlayPanelProps = OverlayPanelProps & {
  op: React.RefObject<OverlayPanel>;
};

export const GetHelpOverlayPanel: React.FC<GetHelpOverlayPanelProps> = ({
  op,
}) => {
  const { t } = useTranslation();
  const account = useAppSelector(selectCurrentAccount);

  return (
    <StyledOverlayPanel ref={op} showCloseIcon={false}>
      <h4>{t('topbar.getHelp')}</h4>
      <FlexContainer justify="flex-start">
        <p>{t('topbar.getHelp.cyberpedia')}</p>
        <a
          className="mb-3 mt-3 ml-1"
          href={CYBERPEDIA_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="cyberpedia-link">{t('topbar.cyberpedia')}</span>
          <i className="pi pi-external-link x2 ml-2" />
        </a>
      </FlexContainer>

      <p>{t('topbar.getHelp.forQuestions')}</p>
      <a
        className="support-email"
        href={`mailto:${
          account?.isFreeTrial || !account?.customerSuccessManager
            ? INFO_EMAIL
            : SUPPORT_EMAIL
        }`}
      >
        {account?.isFreeTrial || !account?.customerSuccessManager
          ? INFO_EMAIL
          : SUPPORT_EMAIL}
      </a>

      {!account?.customerSuccessManager?.name || !!account?.freeTrialEndsAt ? (
        <p className="csm-phone">{SUPPORT_PHONE}</p>
      ) : (
        <>
          <p className="mb-2 mt-3">{t('topbar.getHelp.csm')}:</p>
          <FlexContainer justify="flex-start" align="center">
            <StyledAvatar
              image={account?.customerSuccessManager?.avatar}
              icon="pi pi-user"
              shape="square"
              size="xlarge"
            />
            <div className="ml-3">
              <p className="csm-name mt-0">
                {account?.customerSuccessManager?.name}
              </p>
              <p className="csm-phone">
                {account?.customerSuccessManager?.phone}
              </p>
              <a
                className="csm-email mb-0"
                href={`mailto:${account?.customerSuccessManager?.email}`}
              >
                {account?.customerSuccessManager?.email}
              </a>
            </div>
          </FlexContainer>
        </>
      )}
    </StyledOverlayPanel>
  );
};
